import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ChevronDownIcon, DocumentTextIcon, CurrencyDollarIcon, ChartBarIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';
import { transformSalesData, calculateSalesStats } from '../../utils/salesDataUtils';
import { transformPurchasesData, calculatePurchasesStats } from '../../utils/purchasesDataUtils';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';

const FinancialReports = ({ onBack, documents, customers, suppliers, sales, purchases }) => {
  const [selectedReport, setSelectedReport] = useState('profit-loss');
  const [periodFilter, setPeriodFilter] = useState('month');
  const [isGenerating, setIsGenerating] = useState(false);
  const { companySettings } = useCompanySettings();
  
  // Types de rapports financiers
  const reportTypes = [
    {
      id: 'profit-loss',
      name: 'Compte de résultat',
      description: 'Analyse des revenus, dépenses et résultat net sur la période sélectionnée.',
      icon: CurrencyDollarIcon
    },
    {
      id: 'margin-analysis',
      name: 'Analyse des marges',
      description: 'Rapport détaillé sur les marges brutes et nettes par produit et catégorie.',
      icon: ChartBarIcon
    },
    {
      id: 'cash-flow',
      name: 'Flux de trésorerie',
      description: 'Analyse des entrées et sorties de trésorerie et solde disponible.',
      icon: ArrowTrendingUpIcon
    },
    {
      id: 'unpaid-invoices',
      name: 'Factures impayées',
      description: 'Liste des factures en attente de paiement avec délais de retard.',
      icon: ArrowTrendingDownIcon
    }
  ];
  
  // Calculate REAL profit/loss data from sales and purchases
  const profitLossData = useMemo(() => {
    console.log('🔄 Calculating REAL financial data:', {
      salesCount: sales?.length || 0,
      purchasesCount: purchases?.length || 0,
      documentsCount: documents?.length || 0
    });

    // Transform real sales data
    const transformedSales = transformSalesData(sales || [], customers);
    const salesStats = calculateSalesStats(transformedSales);
    
    // Transform real purchases data
    const transformedPurchases = transformPurchasesData(purchases || [], suppliers);
    const purchasesStats = calculatePurchasesStats(transformedPurchases);

    // Calculate revenue from REAL sales ONLY (no mock data)
    const productSales = salesStats.totalAmount || 0;
    // Only show revenue categories where we have real data
    const totalRevenue = productSales;

    // Calculate expenses from REAL purchases ONLY (no mock data)
    const merchandisePurchases = purchasesStats.totalAmount || 0;
    // Only show expense categories where we have real data
    const totalExpenses = merchandisePurchases;

    // Calculate net result
    const netResult = totalRevenue - totalExpenses;

    console.log('📊 Real financial calculations:', {
      totalRevenue,
      totalExpenses,
      netResult,
      productSales,
      merchandisePurchases
    });

    return {
    revenue: {
      title: 'Revenus',
      items: [
          { label: 'Ventes de produits', value: `${Math.round(productSales).toLocaleString('fr-FR')} DA` }
          // Only showing revenue categories where we have real data
          // Other categories (Services, Autres revenus) would be added when additional revenue tracking is implemented
        ],
        total: `${Math.round(totalRevenue).toLocaleString('fr-FR')} DA`
    },
    expenses: {
      title: 'Dépenses',
      items: [
          { label: 'Achats de marchandises', value: `${Math.round(merchandisePurchases).toLocaleString('fr-FR')} DA` }
          // Only showing expense categories where we have real data
          // Other categories (Salaires, Loyer, Marketing, etc.) would be added when expense tracking is implemented
        ],
        total: `${Math.round(totalExpenses).toLocaleString('fr-FR')} DA`
    },
    result: {
      title: 'Résultat',
        total: `${Math.round(netResult).toLocaleString('fr-FR')} DA`,
        isPositive: netResult >= 0
    }
  };
  }, [sales, purchases, customers, suppliers]);
  
  // Calculate real financial data with memoization to prevent flashing
  const financialData = useMemo(() => {
    console.log('🔄 Calculating enhanced financial data using REAL sales and purchases');
    
    // Use REAL sales and purchases data instead of just documents
    const transformedSales = transformSalesData(sales || [], customers);
    const transformedPurchases = transformPurchasesData(purchases || [], suppliers);
    const salesStats = calculateSalesStats(transformedSales);
    const purchasesStats = calculatePurchasesStats(transformedPurchases);
    
    // Also get documents data for additional context
    const invoices = documents?.filter(doc => doc.type === 'invoice') || [];
    const purchaseOrders = documents?.filter(doc => doc.type === 'purchase-order') || [];
    
    // Calculate margin analysis by category using REAL sales data
    const categoryStats = {};
    transformedSales.forEach(sale => {
      sale.items?.forEach(item => {
        const category = item.category || 'Général';
        if (!categoryStats[category]) {
          categoryStats[category] = { revenue: 0, cost: 0 };
        }
        const itemRevenue = item.total || (item.quantity * item.price);
        const itemCost = itemRevenue * 0.6; // Assume 60% cost ratio (could be improved with real cost data)
        categoryStats[category].revenue += itemRevenue;
        categoryStats[category].cost += itemCost;
      });
    });
    
    const marginAnalysisData = Object.entries(categoryStats).map(([category, stats]) => {
      const margin = stats.revenue > 0 ? ((stats.revenue - stats.cost) / stats.revenue * 100) : 0;
      const netMargin = stats.revenue - stats.cost;
      return {
        category,
        revenue: `${Math.round(stats.revenue).toLocaleString('fr-FR')} DA`,
        cost: `${Math.round(stats.cost).toLocaleString('fr-FR')} DA`,
        margin: `${margin.toFixed(1)}%`,
        netMargin: `${Math.round(netMargin).toLocaleString('fr-FR')} DA`
      };
    });

    // Calculate cash flow data using REAL sales and purchases
    const totalRevenue = salesStats.totalAmount || 0;
    const totalPurchases = purchasesStats.totalAmount || 0;
    
  const cashFlowData = {
    inflows: {
      title: 'Entrées',
      items: [
          { label: 'Encaissements clients', value: `${Math.round(totalRevenue).toLocaleString('fr-FR')} DA` }
          // Only showing cash flow categories where we have real data
          // Other categories (Remboursements, Autres entrées) would be added when payment tracking is implemented
        ],
        total: `${Math.round(totalRevenue).toLocaleString('fr-FR')} DA`
    },
    outflows: {
      title: 'Sorties',
      items: [
          { label: 'Paiements fournisseurs', value: `${Math.round(totalPurchases).toLocaleString('fr-FR')} DA` }
          // Only showing cash flow categories where we have real data
          // Other categories (Salaires, Loyer, Impôts, etc.) would be added when expense tracking is implemented
        ],
        total: `${Math.round(totalPurchases).toLocaleString('fr-FR')} DA`
    },
    balance: {
      title: 'Solde de trésorerie',
        total: `${Math.round(totalRevenue - totalPurchases).toLocaleString('fr-FR')} DA`,
        isPositive: (totalRevenue - totalPurchases) >= 0
      }
    };

    // Calculate unpaid invoices
    const unpaidInvoicesData = invoices
      .filter(inv => ['draft', 'pending', 'confirmed'].includes(inv.status))
      .slice(0, 10)
      .map(invoice => {
        const customer = customers?.find(c => c.id === invoice.clientId);
        const customerName = invoice.contactName || 
                           customer?.name || 
                           `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim() || 
                           'Client inconnu';
        
        const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : new Date(invoice.date);
        const today = new Date();
        const daysDiff = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
        
        let status = 'À échéance';
        if (daysDiff > 30) status = 'En retard (30j+)';
        else if (daysDiff > 15) status = 'En retard (15j+)';
        else if (daysDiff > 0) status = 'En retard (3j+)';
        
        return {
          invoice: invoice.number,
          client: customerName,
          amount: `${(invoice.total || 0).toLocaleString()} DA`,
          dueDate: dueDate.toLocaleDateString('fr-FR'),
          status,
          actions: daysDiff > 0 ? 'À relancer' : '-'
        };
      });

    return { marginAnalysisData, cashFlowData, unpaidInvoicesData };
  }, [documents, customers, suppliers, sales, purchases]); // Only recalculate when data changes

  const { marginAnalysisData, cashFlowData, unpaidInvoicesData } = financialData;

  // AGGRESSIVE formatting function to force comma separators
  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '0 DA';
    
    // Convert to number and round
    const number = Math.round(Number(amount));
    
    // Manual comma insertion - completely bypass any locale formatting
    let str = number.toString();
    let result = '';
    let count = 0;
    
    // Insert commas from right to left
    for (let i = str.length - 1; i >= 0; i--) {
      if (count === 3) {
        result = ',' + result;
        count = 0;
      }
      result = str[i] + result;
      count++;
    }
    
    const finalResult = `${result} DA`;
    console.log(`💰 AGGRESSIVE Formatting ${amount} → ${number} → ${result} → ${finalResult}`);
    return finalResult;
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    try {
      console.log('🔄 Starting Financial PDF generation...');
      console.log('🔧 formatCurrency function exists:', typeof formatCurrency);
      
      // TEST our formatting function first
      console.log('🧪 Testing formatCurrency function:');
      try {
        console.log('Test 10600:', formatCurrency(10600));
        console.log('Test 1800:', formatCurrency(1800));
        console.log('Test 8800:', formatCurrency(8800));
      } catch (error) {
        console.error('❌ Error testing formatCurrency:', error);
      }
      
      console.log('📊 Data check:', {
        salesCount: sales?.length || 0,
        purchasesCount: purchases?.length || 0,
        documentsCount: documents?.length || 0,
        marginAnalysisCount: marginAnalysisData?.length || 0,
        unpaidInvoicesCount: unpaidInvoicesData?.length || 0
      });
      
      // Minimalist red theme colors (EXACTLY like Sales/Purchases)
      const primaryRed = [185, 28, 28]; // #B91C1C - True red color
      const secondaryRed = [153, 27, 27]; // #991B1B - Darker red
      const lightGray = [248, 248, 248]; // Very light gray for subtle backgrounds
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentPage = 1;
      
      // Helper function to add minimal header and footer (EXACTLY like Sales)
      const addHeaderFooter = async () => {
        // HEADER - Company logo and name (EXACTLY like Sales)
        let headerYPosition = 10;
        
        // Add company logo if available
        if (companySettings?.logo) {
          try {
            // Create image from logo data (base64)
            const logoImg = new Image();
            logoImg.src = companySettings.logo;
            
            // Add logo to PDF (small size: 15mm width, proportional height)
            pdf.addImage(companySettings.logo, 'PNG', 20, 5, 15, 10);
            
            // Position company name next to logo
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 40, 12);
            
            headerYPosition = 18; // Adjust for logo height
          } catch (error) {
            console.log('Error adding logo to PDF:', error);
            // Fallback to text-only header
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 20, 10);
          }
        } else {
          // No logo - just company name
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0); // Black color
          const companyName = companySettings?.companyName || 'INVENTAIRE V2';
          pdf.text(companyName, 20, 10);
        }
        
        // Simple thin line below header
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.2);
        pdf.line(20, headerYPosition, pageWidth - 20, headerYPosition);
        
        // FOOTER - Software name and page number
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(120, 120, 120);
        pdf.text('INVENTAIRE V2', 20, pageHeight - 8);
        
        // Page number
        pdf.text(`${currentPage}`, pageWidth - 25, pageHeight - 8);
      };
      
      // Helper function to add minimal section header (EXACTLY like Sales)
      const addSectionHeader = async (title) => {
        if (yPosition > 250) {
          pdf.addPage();
          currentPage++;
          await addHeaderFooter();
          yPosition = 25; // Adjust for new header size
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0); // Black color
        pdf.text(title, 20, yPosition);
        yPosition += 5;
        
        // Simple clean line
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.5);
        pdf.line(20, yPosition, 80, yPosition);
        
        yPosition += 10;
        
        // Reset to black for content
        pdf.setTextColor(0, 0, 0);
      };
      
      // Add header/footer to first page
      await addHeaderFooter();
      let yPosition = 30; // Start below larger header with logo
      
      // 1. MINIMAL TITLE (EXACTLY like Sales)
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Black color
      pdf.text('RAPPORT FINANCIER', 20, yPosition);
      yPosition += 10;
      
      // Simple date info
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(120, 120, 120);
      pdf.text(`${new Date().toLocaleDateString('fr-FR')} ${new Date().toLocaleTimeString('fr-FR')}`, 20, yPosition);
      yPosition += 15;
      
      // 2. PROFIT & LOSS STATEMENT
      await addSectionHeader('COMPTE DE RÉSULTAT');
      
      // Get raw values and apply consistent formatting
      const transformedSales = transformSalesData(sales || [], customers);
      const salesStats = calculateSalesStats(transformedSales);
      const transformedPurchases = transformPurchasesData(purchases || [], suppliers);
      const purchasesStats = calculatePurchasesStats(transformedPurchases);
      
      const productSales = salesStats.totalAmount || 0;
      const totalRevenue = productSales;
      const merchandisePurchases = purchasesStats.totalAmount || 0;
      const totalExpenses = merchandisePurchases;
      const netResult = totalRevenue - totalExpenses;
      
      const profitLossTableData = [
        ['Revenus', ''],
        ['Ventes de produits', formatCurrency(productSales)],
        ['Total Revenus', formatCurrency(totalRevenue)],
        ['', ''],
        ['Dépenses', ''],
        ['Achats de marchandises', formatCurrency(merchandisePurchases)],
        ['Total Dépenses', formatCurrency(totalExpenses)],
        ['', ''],
        ['Résultat Net', formatCurrency(netResult)]
      ];
      
      console.log('📋 Profit/Loss table data:', profitLossTableData);
      
      autoTable(pdf, {
        startY: yPosition,
        head: [['Compte', 'Montant']],
        body: profitLossTableData,
        theme: 'plain',
        headStyles: { 
          fillColor: [255, 255, 255], 
          textColor: [0, 0, 0], 
          fontSize: 10, 
          fontStyle: 'bold',
          lineWidth: 0.1,
          lineColor: [200, 200, 200]
        },
        bodyStyles: { 
          fontSize: 9,
          lineWidth: 0.1,
          lineColor: [230, 230, 230]
        },
        columnStyles: {
          0: { cellWidth: 100, fontStyle: 'normal' },
          1: { cellWidth: 60, halign: 'right' }
        },
        margin: { left: 20, right: 20 }
      });
      
      yPosition = pdf.lastAutoTable.finalY + 15;
      
      // 3. CASH FLOW ANALYSIS
      if (cashFlowData) {
        await addSectionHeader('FLUX DE TRÉSORERIE');
        
              // Use the same sales/purchases values with custom formatting
      const cashFlowTableData = [
        ['Entrées', ''],
        ['Encaissements clients', formatCurrency(totalRevenue)],
        ['Total Entrées', formatCurrency(totalRevenue)],
        ['', ''],
        ['Sorties', ''],
        ['Paiements fournisseurs', formatCurrency(totalExpenses)],
        ['Total Sorties', formatCurrency(totalExpenses)],
        ['', ''],
        ['Solde de Trésorerie', formatCurrency(totalRevenue - totalExpenses)]
      ];
      
      console.log('💰 Cash flow table data:', cashFlowTableData);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Flux', 'Montant']],
          body: cashFlowTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: [255, 255, 255], 
            textColor: [0, 0, 0], 
            fontSize: 10, 
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [200, 200, 200]
          },
          bodyStyles: { 
            fontSize: 9,
            lineWidth: 0.1,
            lineColor: [230, 230, 230]
          },
          columnStyles: {
            0: { cellWidth: 100 },
            1: { cellWidth: 60, halign: 'right' }
          },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      }
      
      // 4. MARGIN ANALYSIS
      console.log('🔍 Margin analysis check:', {
        marginAnalysisDataLength: marginAnalysisData?.length || 0,
        marginAnalysisData: marginAnalysisData,
        salesLength: sales?.length || 0,
        transformedSalesWillHaveData: sales && sales.length > 0
      });
      
      if (marginAnalysisData.length > 0) {
        console.log('✅ Using existing marginAnalysisData for PDF');
        await addSectionHeader('ANALYSE DES MARGES');
        
        // Recalculate margin data with proper formatting
        const transformedSales = transformSalesData(sales || [], customers);
        const categoryStats = {};
        transformedSales.forEach(sale => {
          sale.items?.forEach(item => {
            const category = item.category || 'Général';
            if (!categoryStats[category]) {
              categoryStats[category] = { revenue: 0, cost: 0 };
            }
            const itemRevenue = item.total || (item.quantity * item.price);
            const itemCost = itemRevenue * 0.6; // Assume 60% cost ratio
            categoryStats[category].revenue += itemRevenue;
            categoryStats[category].cost += itemCost;
          });
        });
        
        const marginTableData = Object.entries(categoryStats).map(([category, stats]) => {
          const margin = stats.revenue > 0 ? ((stats.revenue - stats.cost) / stats.revenue * 100) : 0;
          const netMargin = stats.revenue - stats.cost;
          const formattedRevenue = formatCurrency(stats.revenue);
          const formattedCost = formatCurrency(stats.cost);
          const formattedNetMargin = formatCurrency(netMargin);
          
          console.log(`📊 Margin row: ${category} - Revenue: ${formattedRevenue}, Cost: ${formattedCost}, Net: ${formattedNetMargin}`);
          
          return [
            category,
            formattedRevenue,
            formattedCost,
            `${margin.toFixed(1)}%`,
            formattedNetMargin
          ];
        });
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Catégorie', 'Revenus', 'Coûts', 'Marge %', 'Marge Nette']],
          body: marginTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: [255, 255, 255], 
            textColor: [0, 0, 0], 
            fontSize: 10, 
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [200, 200, 200]
          },
          bodyStyles: { 
            fontSize: 9,
            lineWidth: 0.1,
            lineColor: [230, 230, 230]
          },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      } else {
        console.log('❌ marginAnalysisData is empty, forcing margin analysis generation');
        
        // Force margin analysis generation even if marginAnalysisData is empty
        const transformedSales = transformSalesData(sales || [], customers);
        console.log('🔄 Transformed sales for forced margin analysis:', transformedSales);
        
        if (transformedSales && transformedSales.length > 0) {
          await addSectionHeader('ANALYSE DES MARGES');
          
          const categoryStats = {};
          transformedSales.forEach(sale => {
            sale.items?.forEach(item => {
              const category = item.category || 'Général';
              if (!categoryStats[category]) {
                categoryStats[category] = { revenue: 0, cost: 0 };
              }
              const itemRevenue = item.total || (item.quantity * item.price);
              const itemCost = itemRevenue * 0.6; // Assume 60% cost ratio
              categoryStats[category].revenue += itemRevenue;
              categoryStats[category].cost += itemCost;
            });
          });
          
          const marginTableData = Object.entries(categoryStats).map(([category, stats]) => {
            const margin = stats.revenue > 0 ? ((stats.revenue - stats.cost) / stats.revenue * 100) : 0;
            const netMargin = stats.revenue - stats.cost;
            const formattedRevenue = formatCurrency(stats.revenue);
            const formattedCost = formatCurrency(stats.cost);
            const formattedNetMargin = formatCurrency(netMargin);
            
            console.log(`📊 FORCED Margin row: ${category} - Revenue: ${formattedRevenue}, Cost: ${formattedCost}, Net: ${formattedNetMargin}`);
            
            return [
              category,
              formattedRevenue,
              formattedCost,
              `${margin.toFixed(1)}%`,
              formattedNetMargin
            ];
          });
          
          autoTable(pdf, {
            startY: yPosition,
            head: [['Catégorie', 'Revenus', 'Coûts', 'Marge %', 'Marge Nette']],
            body: marginTableData,
            theme: 'plain',
            headStyles: { 
              fillColor: [255, 255, 255], 
              textColor: [0, 0, 0], 
              fontSize: 10, 
              fontStyle: 'bold',
              lineWidth: 0.1,
              lineColor: [200, 200, 200]
            },
            bodyStyles: { 
              fontSize: 9,
              lineWidth: 0.1,
              lineColor: [230, 230, 230]
            },
            margin: { left: 20, right: 20 }
          });
          
          yPosition = pdf.lastAutoTable.finalY + 15;
        } else {
          console.log('⚠️ No sales data available for margin analysis');
        }
      }
      
      // 5. UNPAID INVOICES
      if (unpaidInvoicesData.length > 0) {
        await addSectionHeader('FACTURES IMPAYÉES');
        
        // Recalculate unpaid invoices with proper formatting
        const invoices = documents?.filter(doc => doc.type === 'invoice') || [];
        const unpaidInvoices = invoices
          .filter(inv => ['draft', 'pending', 'confirmed'].includes(inv.status))
          .slice(0, 10);
        
        const invoicesTableData = unpaidInvoices.map(invoice => {
          const customer = customers?.find(c => c.id === invoice.clientId);
          const customerName = invoice.contactName || 
                             customer?.name || 
                             `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim() || 
                             'Client inconnu';
          
          const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : new Date(invoice.date);
          const today = new Date();
          const daysDiff = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
          
          let status = 'À échéance';
          if (daysDiff > 30) status = 'En retard (30j+)';
          else if (daysDiff > 15) status = 'En retard (15j+)';
          else if (daysDiff > 0) status = 'En retard (3j+)';
          
          return [
            invoice.number,
            customerName,
            formatCurrency(invoice.total || 0),
            dueDate.toLocaleDateString('fr-FR'),
            status
          ];
        });
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Facture', 'Client', 'Montant', 'Échéance', 'Statut']],
          body: invoicesTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: [255, 255, 255], 
            textColor: [0, 0, 0], 
            fontSize: 10, 
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [200, 200, 200]
          },
          bodyStyles: { 
            fontSize: 9,
            lineWidth: 0.1,
            lineColor: [230, 230, 230]
          },
          margin: { left: 20, right: 20 }
        });
      }
      
      // 6. FINAL PAGE SETUP - Add header/footer to all pages
      const totalPages = pdf.getNumberOfPages();
      for (let i = 2; i <= totalPages; i++) {
        pdf.setPage(i);
        currentPage = i;
        await addHeaderFooter();
      }
      
      // Generate filename and download
      const fileName = `Rapport_Financier_Complet_${new Date().toISOString().slice(0, 10)}.pdf`;
      console.log('💾 Saving PDF:', fileName);
      
      pdf.save(fileName);
      
      setIsGenerating(false);
      toast.success('Rapport PDF financier généré avec succès!');
      console.log('✅ Financial PDF generation completed successfully');
      
    } catch (error) {
      console.error('❌ Financial PDF generation error:', error);
      setIsGenerating(false);
      toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };

  // Rendu du contenu du rapport sélectionné
  const renderReportContent = () => {
    switch (selectedReport) {
      case 'profit-loss':
        return (
          <div className="space-y-6">
            {/* Revenus */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-3">{profitLossData.revenue.title}</h4>
              <div className="space-y-2">
                {profitLossData.revenue.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-white/80">{item.label}</span>
                    <span className="text-white">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 mt-3 pt-3 flex justify-between items-center font-medium">
                <span className="text-white">Total revenus</span>
                <span className="text-green-400">{profitLossData.revenue.total}</span>
              </div>
            </div>
            
            {/* Dépenses */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-3">{profitLossData.expenses.title}</h4>
              <div className="space-y-2">
                {profitLossData.expenses.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-white/80">{item.label}</span>
                    <span className="text-white">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 mt-3 pt-3 flex justify-between items-center font-medium">
                <span className="text-white">Total dépenses</span>
                <span className="text-red-400">{profitLossData.expenses.total}</span>
              </div>
            </div>
            
            {/* Résultat */}
            <div className={`bg-gradient-to-r ${profitLossData.result.isPositive 
              ? 'from-green-500/20 to-blue-500/20 border-green-500/30' 
              : 'from-red-500/20 to-orange-500/20 border-red-500/30'
            } border rounded-xl p-4`}>
              <div className="flex justify-between items-center font-bold">
                <span className="text-white">{profitLossData.result.title}</span>
                <span className={`text-lg ${profitLossData.result.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                  {profitLossData.result.total}
                </span>
              </div>
              <div className="mt-2 text-sm text-white/60">
                {profitLossData.result.isPositive ? '✅ Entreprise rentable' : '⚠️ Perte à surveiller'}
              </div>
            </div>
          </div>
        );
      
      case 'margin-analysis':
        return (
          <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-xl">
            <table className="w-full">
              <thead className="bg-white/10">
                <tr>
                  <th className="px-4 py-3 text-left text-white font-medium">Catégorie</th>
                  <th className="px-4 py-3 text-right text-white font-medium">CA</th>
                  <th className="px-4 py-3 text-right text-white font-medium">Coût</th>
                  <th className="px-4 py-3 text-right text-white font-medium">Marge</th>
                  <th className="px-4 py-3 text-right text-white font-medium">Marge nette</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {marginAnalysisData.length > 0 ? (
                  marginAnalysisData.map((item, index) => (
                  <tr key={index} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-white font-medium">{item.category}</td>
                    <td className="px-4 py-3 text-white text-right">{item.revenue}</td>
                    <td className="px-4 py-3 text-white text-right">{item.cost}</td>
                    <td className="px-4 py-3 text-green-400 text-right font-medium">{item.margin}</td>
                    <td className="px-4 py-3 text-green-400 text-right font-medium">{item.netMargin}</td>
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-white/60">
                      Aucune donnée de marge disponible. Ajoutez des ventes pour voir l'analyse des marges.
                    </td>
                  </tr>
                )}
                {marginAnalysisData.length > 0 && (() => {
                  const totalRevenue = marginAnalysisData.reduce((sum, item) => {
                    return sum + (parseFloat(item.revenue.replace(/[^\d]/g, '')) || 0);
                  }, 0);
                  const totalCost = marginAnalysisData.reduce((sum, item) => {
                    return sum + (parseFloat(item.cost.replace(/[^\d]/g, '')) || 0);
                  }, 0);
                  const totalMargin = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue * 100) : 0;
                  const totalNetMargin = totalRevenue - totalCost;
                  
                  return (
                <tr className="bg-white/10 font-bold">
                  <td className="px-4 py-3 text-white">Total</td>
                      <td className="px-4 py-3 text-white text-right">{Math.round(totalRevenue).toLocaleString('fr-FR')} DA</td>
                      <td className="px-4 py-3 text-white text-right">{Math.round(totalCost).toLocaleString('fr-FR')} DA</td>
                      <td className="px-4 py-3 text-green-400 text-right font-medium">{totalMargin.toFixed(1)}%</td>
                      <td className="px-4 py-3 text-green-400 text-right font-medium">{Math.round(totalNetMargin).toLocaleString('fr-FR')} DA</td>
                </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        );
        
      case 'cash-flow':
        return (
          <div className="space-y-6">
            {/* Entrées */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-3">{cashFlowData.inflows.title}</h4>
              <div className="space-y-2">
                {cashFlowData.inflows.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-white/80">{item.label}</span>
                    <span className="text-white">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 mt-3 pt-3 flex justify-between items-center font-medium">
                <span className="text-white">Total entrées</span>
                <span className="text-green-400">{cashFlowData.inflows.total}</span>
              </div>
            </div>
            
            {/* Sorties */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-3">{cashFlowData.outflows.title}</h4>
              <div className="space-y-2">
                {cashFlowData.outflows.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-white/80">{item.label}</span>
                    <span className="text-white">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 mt-3 pt-3 flex justify-between items-center font-medium">
                <span className="text-white">Total sorties</span>
                <span className="text-red-400">{cashFlowData.outflows.total}</span>
              </div>
            </div>
            
            {/* Solde */}
            <div className={`bg-gradient-to-r ${cashFlowData.balance.isPositive 
              ? 'from-green-500/20 to-blue-500/20 border-green-500/30' 
              : 'from-red-500/20 to-orange-500/20 border-red-500/30'
            } border rounded-xl p-4`}>
              <div className="flex justify-between items-center font-bold">
                <span className="text-white">{cashFlowData.balance.title}</span>
                <span className={`text-lg ${cashFlowData.balance.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                  {cashFlowData.balance.total}
                </span>
              </div>
              <div className="mt-2 text-sm text-white/60">
                {cashFlowData.balance.isPositive ? '✅ Trésorerie positive' : '⚠️ Trésorerie déficitaire'}
              </div>
            </div>
          </div>
        );
        
      case 'unpaid-invoices':
        return (
          <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-xl">
            <table className="w-full">
              <thead className="bg-white/10">
                <tr>
                  <th className="px-4 py-3 text-left text-white font-medium">Facture</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Client</th>
                  <th className="px-4 py-3 text-right text-white font-medium">Montant</th>
                  <th className="px-4 py-3 text-center text-white font-medium">Échéance</th>
                  <th className="px-4 py-3 text-center text-white font-medium">Statut</th>
                  <th className="px-4 py-3 text-left text-white font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {unpaidInvoicesData.length > 0 ? (
                  unpaidInvoicesData.map((item, index) => (
                  <tr key={index} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-white font-mono text-sm">{item.invoice}</td>
                    <td className="px-4 py-3 text-white">{item.client}</td>
                    <td className="px-4 py-3 text-white text-right font-medium">{item.amount}</td>
                    <td className="px-4 py-3 text-white text-center">{item.dueDate}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.status.includes('30j+') 
                          ? 'bg-red-500/20 text-red-400' 
                          : item.status.includes('15j+')
                          ? 'bg-orange-500/20 text-orange-400'
                          : item.status.includes('retard')
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/80 text-sm">{item.actions}</td>
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-white/60">
                      ✅ Aucune facture impayée. Toutes les factures sont à jour !
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
        
      default:
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <DocumentTextIcon className="h-16 w-16 text-white/30 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Rapport non disponible</h3>
            <p className="text-white/60 max-w-md">
              Le rapport sélectionné n'est pas disponible actuellement.
            </p>
          </div>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header with back button and export button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Rapports financiers</h1>
          <p className="text-white/60 text-sm mt-1">
            Analysez vos performances financières et votre rentabilité
          </p>
        </div>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleGenerateReport}
          disabled={isGenerating}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
        >
          {isGenerating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Génération...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Exporter PDF Complet</span>
            </>
          )}
        </motion.button>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left sidebar with report types */}
        <div className="md:col-span-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-white">Types de rapports</h3>
            
            <div className="space-y-2">
              {reportTypes.map((report) => (
                <div
                  key={report.id}
                  onClick={() => setSelectedReport(report.id)}
                  className={`flex items-center space-x-3 cursor-pointer p-4 rounded-xl transition-colors ${
                    selectedReport === report.id
                      ? 'bg-gradient-to-r from-amber-500/30 to-amber-600/30 border border-amber-500/50'
                      : 'hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${selectedReport === report.id ? 'bg-amber-500/30' : 'bg-white/10'}`}>
                    <report.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">{report.name}</h4>
                    <p className="text-xs text-white/60 mt-0.5">{report.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right content area */}
        <div className="md:col-span-8">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">
                {reportTypes.find(r => r.id === selectedReport)?.name}
              </h3>
              

            </div>
            
            {/* Report content */}
            {renderReportContent()}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FinancialReports; 